import { useLocation, useParams } from "react-router-dom";

// Subdomains that are NOT dealers: the apex/marketing site, the admin platform, and infra hosts.
// A request to any of these must never be treated as a tenant slug. `visualizer` is the shared
// path-mode host — `visualizer.renoglance.com/<slug>` resolves the tenant from the path, so it must
// NOT be read as a tenant subdomain itself. `renoglance` and `manufacturer` are the public demo
// catalogue scopes (path-mode only) and can never be dealer subdomains either.
const RESERVED = new Set(["www", "platform", "api", "app", "cdn", "assets", "static", "visualizer", "renoglance", "manufacturer"]);

/** The reserved path token of the all-manufacturers catalogue demo (`/renoglance`). */
export const DEMO_SLUG = "renoglance";

/** The reserved child segment that opens a dealer's full active-catalogue "showroom" mode
 *  (`/{slug}/showroom` in path mode, or `/showroom` on a tenant subdomain). */
export const SHOWROOM_SEGMENT = "showroom";
/** Explicit priced-mode segment — exists so a priced link keeps working for tenants whose
 * configured DEFAULT is showroom (`/{slug}` follows the tenant setting server-side). */
export const PRICED_SEGMENT = "priced";

/** What the route forces: "full" = showroom, "own" = priced, null = follow the tenant default. */
export type CatalogueRouteMode = "full" | "own" | null;

const IP_HOST = /^\d{1,3}(\.\d{1,3}){3}$/;

/**
 * Resolve the tenant from the host when the visualizer is served on a per-tenant subdomain
 * (e.g. `accuratedoor.renoglance.com` → `"accuratedoor"`). Returns null on the apex, on a reserved
 * subdomain, on a bare host (`localhost`), or on an IP — in which case the app falls back to the
 * path-based `/:tenantSlug` routes. Works for local testing via `*.lvh.me` / `*.localhost` too,
 * since the rule is purely "3+ labels, first label not reserved, not an IP".
 */
export function getSubdomainTenant(): string | null {
  const host = window.location.hostname;
  if (IP_HOST.test(host)) return null;
  const parts = host.split(".");
  if (parts.length < 3) return null; // apex (renoglance.com) or bare host → path mode
  const label = parts[0].toLowerCase();
  if (!label || RESERVED.has(label)) return null;
  return label;
}

export type CatalogScopeKind = "tenant" | "demo" | "manufacturer";

export interface CatalogScope {
  kind: CatalogScopeKind;
  /**
   * The URL path token of the scope: a tenant slug, `renoglance`, or `manufacturer/{slug}`. This is
   * what both the API base (`/api/v1/visualizer/{token}/…`) and in-app paths (`/{token}/…`) are
   * built from, so a composite token routes correctly everywhere.
   */
  token: string;
  /** Set only for kind === "manufacturer". */
  manufacturerSlug?: string;
}

/**
 * The active catalogue scope, derived from the hosting mode + route params: a dealer's visualizer
 * (`/{tenantSlug}` or subdomain), the all-manufacturers demo (`/renoglance`), or one manufacturer's
 * public catalogue (`/manufacturer/{slug}`). Server behavior (pricing/lead gating) is driven by the
 * branding endpoint's flags — this hook only shapes URLs.
 */
export function useCatalogScope(): CatalogScope {
  const { tenantSlug, manufacturerSlug } = useParams();
  const sub = getSubdomainTenant();
  if (sub) return { kind: "tenant", token: sub };
  if (manufacturerSlug) {
    const slug = manufacturerSlug.toLowerCase();
    return { kind: "manufacturer", token: `manufacturer/${slug}`, manufacturerSlug: slug };
  }
  if ((tenantSlug ?? "").toLowerCase() === DEMO_SLUG) return { kind: "demo", token: DEMO_SLUG };
  return { kind: "tenant", token: tenantSlug ?? "" };
}

/**
 * The active scope token, working in every hosting mode: the subdomain when present, otherwise the
 * `:tenantSlug` / `manufacturer/:manufacturerSlug` route params. Drop-in replacement for the old
 * `const { tenantSlug = "" } = useParams()` — API calls and `tenantPath()` compose it verbatim.
 */
export function useTenantSlug(): string {
  return useCatalogScope().token;
}

/**
 * Whether the visualizer is in the dealer's full active-catalogue "showroom" mode — a price-free browse
 * of every manufacturer/collection they've enabled — derived from the route. The server enforces the
 * price-off + full-catalogue behavior from the `?catalogue=full` flag the SPA sends; this hook shapes the
 * UI badge and tells the API calls which catalogue to request.
 */
export function useCatalogueMode(): boolean {
  const { pathname } = useLocation();
  return isShowroomPath(pathname);
}

/**
 * The catalogue mode the ROUTE forces, as the `?catalogue=` value the API expects — or null for a
 * bare link, where the server applies the tenant's configured default. This is what API calls
 * forward; UI gating (price box, showroom badge) comes from the server-authoritative branding
 * response instead, so a bare link on a showroom-default tenant still renders showroom.
 */
export function useCatalogueRouteMode(): CatalogueRouteMode {
  const { pathname } = useLocation();
  return routeModeOf(pathname);
}

function isShowroomPath(pathname: string): boolean {
  return routeModeOf(pathname) === "full";
}

/**
 * Pure over the pathname so the hooks (router location) and `tenantPath` (window location,
 * non-hook) share one rule: the mode segment is the first segment on a tenant subdomain
 * (`/showroom/…`, `/priced/…`), else the second (`/{slug}/showroom/…`).
 */
function routeModeOf(pathname: string): CatalogueRouteMode {
  const parts = pathname.split("/").filter(Boolean);
  // Case-insensitive: React Router matches `/Showroom` to the showroom route (its matching is
  // case-insensitive), so a strict compare here would render the showroom page while silently
  // requesting the tenant-DEFAULT catalogue from the API.
  const segment = (getSubdomainTenant() ? parts[0] : parts[1])?.toLowerCase();
  if (segment === SHOWROOM_SEGMENT) return "full";
  if (segment === PRICED_SEGMENT) return "own";
  return null;
}

/**
 * Build an in-app path that is correct for the current hosting mode. In subdomain mode the slug is
 * implicit in the host, so paths omit it (`/visualize`); in path mode the scope token is the leading
 * segment(s) (`/{slug}/visualize`, `/renoglance/visualize`, `/manufacturer/amarr/visualize`). Pass
 * the empty subpath for the scope root.
 */
export function tenantPath(slug: string, subpath = ""): string {
  const tail = subpath ? (subpath.startsWith("/") ? subpath : `/${subpath}`) : "";
  // Keep the mode sticky: once in an explicit mode (/showroom or /priced), every in-app path stays
  // under it so navigating the flow never falls back to the tenant-default experience.
  const routeMode = routeModeOf(window.location.pathname);
  const mode = routeMode === "full" ? `/${SHOWROOM_SEGMENT}` : routeMode === "own" ? `/${PRICED_SEGMENT}` : "";
  if (getSubdomainTenant()) return `${mode}${tail}` || "/";
  return `/${slug}${mode}${tail}`;
}
